import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trophy } from "lucide-react";
import { getPickemEventsOptions } from "@api/generated/@tanstack/react-query.gen";
import type { PickemEvent } from "@api/generated/types.gen";
import { PickemEventCard } from "@/features/pickem/components/pickem-event-card";
import { getEffectiveStatus } from "@/features/pickem/lib/pickem-status";
import {
  draftPreviewEnabled,
  previewEvents,
} from "@/features/pickem/lib/draft-preview";

export const Route = createFileRoute("/pickem/")({
  component: PickemIndexPage,
});

const STATUS_ORDER: Record<string, number> = {
  active: 0,
  locked: 1,
  scheduled: 2,
  completed: 3,
  draft: 4,
};

function PickemIndexPage() {
  const navigate = useNavigate();
  const {
    data: events,
    isLoading,
    error,
  } = useQuery({
    ...getPickemEventsOptions(),
    ...(draftPreviewEnabled ? { queryFn: previewEvents } : {}),
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 0,
  });

  const sortedEvents = (events ?? []).slice().sort((a, b) => {
    const sa = STATUS_ORDER[getEffectiveStatus(a)] ?? 99;
    const sb = STATUS_ORDER[getEffectiveStatus(b)] ?? 99;
    if (sa !== sb) return sa - sb;
    const aDate = a.lockDate ?? a.publishDate ?? a.createdAt;
    const bDate = b.lockDate ?? b.publishDate ?? b.createdAt;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  const handleEventClick = (event: PickemEvent) => {
    navigate({ to: "/pickem/$eventId", params: { eventId: event.id } });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          Fantasy
        </h1>
        <p className="text-sm text-muted-foreground">
          Predict tournament brackets and climb the leaderboard.
        </p>
      </div>

      {draftPreviewEnabled && (
        <p className="rounded-lg border p-3 text-sm">
          Local draft preview. Test picks save only in this browser. Events are
          a snapshot; refresh the export to load CRM changes.
        </p>
      )}
      {error && <p role="alert">{error.message}</p>}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted-foreground">Loading events…</p>
        </div>
      ) : sortedEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Trophy className="h-12 w-12 text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">
            No pickem events available yet — check back soon.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedEvents.map((event) => (
            <PickemEventCard
              key={event.id}
              event={event}
              onClick={handleEventClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
